import { HttpRequest, HttpEvent, HttpInterceptorFn, HttpHandlerFn } from '@angular/common/http';
import { catchError, Observable, switchMap, throwError } from 'rxjs';
import { inject } from '@angular/core';
import { jwtDecode } from 'jwt-decode';

import { environment } from '@env/environment';
import { UserService } from '@app/services/user.service';
import { ToastService } from '@app/services/toast.service';
import { TokenInterface } from '@app/interfaces/token.interface';
import { ApiMethod } from '@app/interfaces/api-request-body.interface';

export const authInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  const userSrv = inject(UserService);
  const toastSrv = inject(ToastService);

  const accessToken = userSrv.accessToken;
  const refreshToken = userSrv.token?.refreshToken;
  const isApiRPCUrl = req.url.includes(environment.appUrl);
  const isApiRestUrl = req.url.includes(environment.restAPI);
  const isRefreshTokenUrl = req.url.includes(ApiMethod.refreshToken);
  const isSignInUrl = req.url.includes(ApiMethod.auth);
  const isAuthUrl = isRefreshTokenUrl || isSignInUrl;

  // Only add Authorization header if request is made to API URL and accessToken is present
  if (isApiRestUrl && accessToken) {
    // Check if access token is expired, refreshToken is present, and request is not for token refresh
    if (isTokenExpired(accessToken) && refreshToken && !isRefreshTokenUrl) {
      // Perform token refresh
      return userSrv.refreshToken(refreshToken).pipe(
        switchMap((token: TokenInterface) => {
          // Clone request and add the new access token to the header
          const newReq = req.clone({
            setHeaders: { Authorization: `Bearer ${token.accessToken}` },
          });
          return next(newReq);
        }),
        catchError((error) => {
          // Set refresh error, logout, and throw error
          toastSrv.showToast('Unauthorized! You need to log in.');
          userSrv.logout();
          return throwError(() => error);
        }),
      );
    }

    // Clone request and set Authorization header with the access token
    req = req.clone({
      headers: req.headers.set('Authorization', `Bearer ${accessToken}`),
    });
  }

  return next(req).pipe(
    catchError((error) => {
      if (error.status === 401 && !isAuthUrl && !isApiRPCUrl) {
        toastSrv.showToast('Unauthorized! You need to log in.');
        userSrv.logout();
      }
      return throwError(() => error);
    }),
  );
};

/**
 * Check if JWT token is expired
 * @param jwtToken
 * @returns boolean - true if expired, false otherwise
 */
function isTokenExpired(jwtToken: string): boolean {
  if (!jwtToken) {
    return true;
  }

  // decode JWT token
  const decodedToken = jwtDecode(jwtToken);
  if (!decodedToken.exp) {
    return true;
  }

  const currentTime = Math.floor(Date.now() / 1000);

  // check the expiration time
  return decodedToken.exp < currentTime;
}
